import { useEffect, useRef, useState } from 'react';
import { X, Volume2, VolumeX, RotateCcw, Play, Pause } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * TutorialVideoModal
 * Plays a native MP4 for a product tutorial. Sibling to SuccessStoryModal
 * but simpler — no CTA and no external-URL fallback. Used inside the
 * logged-in `/tutorials` page.
 */
export default function TutorialVideoModal({ tutorial, onClose }) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);

  const src = tutorial.video_url?.startsWith('http')
    ? tutorial.video_url
    : `${BACKEND_URL}${tutorial.video_url}`;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.muted = true;
    setIsMuted(true);
    vid.play().catch(() => setIsPlaying(false));
  }, []);

  const togglePlay = () => {
    const vid = videoRef.current;
    if (!vid) return;
    if (vid.paused) {
      vid.play().then(() => setIsPlaying(true)).catch(() => {});
    } else {
      vid.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.muted = !vid.muted;
    setIsMuted(vid.muted);
  };

  const replay = () => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.currentTime = 0;
    vid.play().then(() => setIsPlaying(true)).catch(() => {});
  };

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/85 backdrop-blur-md p-3 sm:p-6 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="tutorial-video-modal-overlay"
    >
      <div
        className="relative bg-slate-950 rounded-2xl shadow-2xl border border-white/10 w-full max-w-3xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid={`tutorial-video-modal-${tutorial.key}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-slate-900/80">
          <div className="min-w-0">
            <div className="text-white text-sm font-bold font-['Outfit'] truncate">{tutorial.title}</div>
            <div className="text-white/40 text-[10px] truncate">Tutorial</div>
          </div>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white hover:bg-white/10 rounded-lg p-1.5 transition-colors shrink-0"
            data-testid="tutorial-video-modal-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Player */}
        <div className="relative aspect-video bg-black overflow-hidden" data-testid="tutorial-video-player">
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-contain"
            src={src}
            controls
            playsInline
            preload="metadata"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
            data-testid="tutorial-video-element"
          />

          {/* Extra control cluster — native controls handle most cases, this
              gives a redundant hover-affordance for muted/replay so the
              tutorial feels consistent with our other players. */}
          <div className="absolute top-3 right-3 z-30 flex gap-2 pointer-events-none">
            <button
              onClick={toggleMute}
              className="pointer-events-auto w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur flex items-center justify-center text-white border border-white/10 transition-colors"
              data-testid="tutorial-video-mute-btn"
              aria-label="Toggle mute"
            >
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <button
              onClick={togglePlay}
              className="pointer-events-auto w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur flex items-center justify-center text-white border border-white/10 transition-colors"
              data-testid="tutorial-video-play-btn"
              aria-label="Play/Pause"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
            </button>
            <button
              onClick={replay}
              className="pointer-events-auto w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur flex items-center justify-center text-white border border-white/10 transition-colors"
              data-testid="tutorial-video-replay-btn"
              aria-label="Replay"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Description */}
        {tutorial.description && (
          <div className="bg-slate-900/90 border-t border-white/10 px-4 sm:px-5 py-3">
            <div className="text-white/70 text-xs sm:text-sm leading-relaxed">
              {tutorial.description}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
